<template>
    <v-dialog v-model="dialog" fullscreen hide-overlay @keydown.esc="close()">
        <v-form
            ref="form"
            v-model="form.valid"
            lazy-validation
        >
            <v-card min-height="100vh" id="formdemand">
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click.native="close()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>{{ form.title }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn
                            v-if="!form.loading && !(!form.isEmployee && form.isEdit)"
                            dark
                            text
                            :disabled="!form.valid"
                            :loading="btnDisable"
                            @click.native="save(formMethod)"
                        >{{ $t('common.save') }}</v-btn>
                    </v-toolbar-items>
                </v-toolbar>

                <v-container>
                    <v-card-title>
                        <v-subheader>{{ $t('demand.form.data') }}</v-subheader>
                    </v-card-title>

                    <v-divider></v-divider>

                    <v-progress-linear :indeterminate="true" v-show="form.loading"></v-progress-linear>

                    <div v-show="!form.loading">
                        <v-row>
                            <v-col cols="12" :md="8">
                                <v-text-field
                                    v-model="demand.demand"
                                    :label="form.labels.demand"
                                    :rules="form.rules.demand"
                                    prepend-icon="gavel"
                                    required
                                    filled
                                    dense
                                    :readonly="!form.isEmployee && form.isEdit"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" :md="4" v-if="!form.isExtern">
                                <v-text-field
                                    v-model="demand.requester"
                                    :label="form.labels.requester"
                                    prepend-icon="person"
                                    required
                                    filled
                                    dense
                                    :readonly="!form.isEmployee && form.isEdit"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row v-if="showIsExtern">
                            <v-col cols="3">
                                <div class="d-flex align-center input-height">
                                    <v-switch
                                        flat
                                        inset
                                        v-model="form.isExtern"
                                        :label="$t('demand.form.isExternalDemand')"
                                        :readonly="form.isEdit"
                                    ></v-switch>
                                </div>
                            </v-col>
                            <v-col cols="12" md="4" v-if="form.isExtern">
                                <input-auto-complete :setup.sync="form.selects.manager"></input-auto-complete>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" md="3" v-if="
                                    form.isEmployee &&
                                    !form.isExtern &&
                                    form.companyType === 'manager'
                                ">
                                <input-auto-complete :setup.sync="form.selects.client"></input-auto-complete>
                            </v-col>

                            <v-col cols="12" md="3" v-if="form.isEmployee && !form.isExtern">
                                <input-auto-complete :setup.sync="form.selects.department"></input-auto-complete>
                            </v-col>

                            <v-col cols="12" :md="(!form.isEmployee && controlColVisibility) ? '6' : '3'" v-if="controlColVisibility">
                                <input-auto-complete :setup.sync="form.selects.responsible"></input-auto-complete>
                            </v-col>
                            <v-col cols="12" :md="(!form.isEmployee && controlColVisibility) ? '6' : '3'" v-if="controlColVisibility">
                                <input-select :setup.sync="form.selects.status"></input-select>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col
                                cols="12"
                                :md="((!form.isEmployee && controlColVisibility) || (form.isEmployee && !form.isEdit)) ? '6' : '4'"
                                v-if="controlColVisibility"
                            >
                                <input-date-picker :setup.sync="form.datePickers.startDate"></input-date-picker>
                            </v-col>
                            <v-col
                                cols="12"
                                :md="((!form.isEmployee && controlColVisibility) || (form.isEmployee && !form.isEdit)) ? '6' : '4'"
                                v-if="controlColVisibility"
                            >
                                <input-date-picker :setup.sync="form.datePickers.deadlineDate"></input-date-picker>
                            </v-col>
                            <v-col cols="12" md="4" v-if="form.isEmployee && form.isEdit">
                                <demand-worked-hour v-if="controlColVisibility" :setup.sync="form" :demand.sync="demand" @event-interaction="loadInteractions()"></demand-worked-hour>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-divider class="mb-3"></v-divider>
                                <observation v-model="demand" :isEdit="form.isEdit" :isExtern="form.isExtern"/>
                            </v-col>
                        </v-row>
                        <demand-task v-if="controlColVisibility" :setup.sync="form" :demand.sync="demand" @event-interaction="loadInteractions()"></demand-task>
                        <v-divider class="mb-5"></v-divider>
                        <demand-timeline v-show="controlColVisibility" :setup.sync="form" :demand.sync="demand" class="mb-5"></demand-timeline>
                    </div>
                </v-container>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script>
import API from '@/api'
import InputDatePicker from '@/components/InputDatePicker'
import InputAutoComplete from '@/components/InputAutoComplete'
import InputSelect from '@/components/InputSelect'
import DemandWorkedHour from '@/components/demand/DemandWorkedHour'
import DemandTask from '@/components/demand/DemandTask'
import DemandTimeline from '@/components/demand/DemandTimeline'
import Observation from '@/components/Observation'

export default {
    name: 'form-demand',
    metaInfo () {
        if (this.$route.params.id != null) {
            return {
                title: this.$t('demand.form.metaDetail.title'),
                meta: [
                    { name: 'description', content: this.$t('demand.form.metaDetail.description') },
                    { name: 'keywords', content: this.$t('demand.form.metaDetail.keywords') }
                ]
            }
        } else {
            return {
                title: this.$t('demand.form.metaNew.title'),
                meta: [
                    { name: 'description', content: this.$t('demand.form.metaNew.description') },
                    { name: 'keywords', content: this.$t('demand.form.metaNew.keywords') }
                ]
            }
        }
    },
    components: {
        InputDatePicker,
        InputAutoComplete,
        InputSelect,
        DemandWorkedHour,
        DemandTask,
        DemandTimeline,
        Observation
    },
    directives: {},
    data: function () {
        return {
            btnDisable: false,
            dialog: true,
            tempFiles: [],
            form: {
                loading: true,
                isEdit: false,
                fromDemand: true,
                showAdd: true,
                showUpdate: false,
                valid: false,
                title: this.$t('demand.form.titleNew'),
                isEmployee: true,
                isAdmin: true,
                isExtern: false,
                companyType: undefined,
                newOnly: true,
                labels: {
                    demand: this.$t('demand.form.demand.label'),
                    requester: this.$t('demand.form.requester.label'),
                    workedHours: this.$t('demand.form.workedHours.label')
                },
                rules: {
                    demand: [
                        v => !!v || this.$t('demand.form.demand.required'),
                        v => (v && v.length >= 3) || this.$t('demand.form.demand.length')
                    ]
                },
                datePickers: {
                    startDate: {
                        label: this.$t('demand.form.startDate.label'),
                        rule: [
                            v => !!v || this.$t('demand.form.startDate.required')
                        ],
                        required: true,
                        readonly: false,
                        filled: true,
                        dateFormatted: undefined
                    },
                    deadlineDate: {
                        label: this.$t('demand.form.deadlineDate.labelRequired'),
                        rule: [
                            v => !!v || this.$t('demand.form.startDate.required')
                        ],
                        checkDateisBeforeNow: true,
                        required: true,
                        readonly: false,
                        clearable: false,
                        filled: true,
                        dateFormatted: undefined
                    }
                },
                selects: {
                    manager: {
                        label: this.$t('demand.form.manager.label'),
                        text: 'manager.name',
                        value: 'objectId',
                        icon: 'contacts',
                        required: true,
                        readonly: false,
                        filled: true,
                        multiple: false,
                        rule: [
                            v => !!v || this.$t('demand.form.manager.required')
                        ],
                        items: [],
                        selectedItems: undefined
                    },
                    client: {
                        label: this.$t('demand.form.client.label'),
                        text: 'name',
                        value: 'objectId',
                        icon: 'contacts',
                        required: true,
                        readonly: false,
                        filled: true,
                        multiple: false,
                        rule: [
                            v => !!v || this.$t('demand.form.client.required')
                        ],
                        items: [],
                        selectedItems: undefined
                    },
                    department: {
                        label: this.$t('demand.form.department.label'),
                        text: 'name',
                        value: 'objectId',
                        icon: 'contacts',
                        required: false,
                        readonly: false,
                        filled: true,
                        multiple: false,
                        rule: [],
                        items: [],
                        selectedItems: undefined
                    },
                    responsible: {
                        label: this.$t('demand.form.responsible.label'),
                        text: 'user.name',
                        value: 'objectId',
                        icon: 'person',
                        required: true,
                        readonly: false,
                        multiple: true,
                        filled: true,
                        rule: [
                            v => !!v.length || this.$t('demand.form.responsible.required')
                        ],
                        items: [],
                        selectedItems: []
                    },
                    status: {
                        label: this.$t('demand.form.status.label'),
                        text: 'label',
                        value: 'value',
                        icon: 'feedback',
                        required: true,
                        readonly: false,
                        filled: true,
                        multiple: false,
                        rule: [
                            v => !!v || this.$t('demand.form.status.required')
                        ],
                        items: [
                            { value: 1, label: this.$t('demand.status.pending') },
                            { value: 2, label: this.$t('demand.status.starting') },
                            { value: 3, label: this.$t('demand.status.waiting') },
                            { value: 4, label: this.$t('demand.status.standby') },
                            { value: 5, label: this.$t('demand.status.finished') },
                            { value: 6, label: this.$t('demand.status.canceled') }
                        ],
                        selectedItems: undefined
                    }
                },
                tasks: {
                    loading: false,
                    item: undefined
                },
                timeline: {
                    loadData: false,
                    className: 'Demand'
                },
                dirtyBkp: {
                    responsibles: []
                }
            },
            demand: {
                objectId: undefined,
                demand: undefined,
                requester: undefined,
                client: undefined,
                owner: undefined,
                department: undefined,
                clientName: undefined,
                responsible: undefined,
                responsibleName: undefined,
                createdBy: undefined,
                startDate: undefined,
                deadlineDate: undefined,
                endDate: undefined,
                status: '0',
                workedHours: '0',
                isDoing: true,
                comments: '',
                demandTask: {
                    count: 0,
                    results: []
                },
                interaction: {
                    count: 0,
                    results: []
                },
                files: [],
                mainAccount: false
            }
        }
    },
    watch: {
        'dialog': {
            handler: function (val, oldVal) {
                if (!val) {
                    let root = document.documentElement
                    root.classList.remove('overflow-y-hidden')
                }
            },
            deep: true
        },
        'form.selects.status.selectedItems': {
            handler: function (val, oldVal) {
                if (oldVal !== val) {
                    if (this.form.isEmployee) {
                        if (val === 1 || val === 4 || val === 6) {
                            this.form.datePickers.deadlineDate.label = this.$t('demand.form.deadlineDate.label')
                            this.form.datePickers.deadlineDate.clearable = true
                            this.form.datePickers.deadlineDate.rule = []
                        } else {
                            this.form.datePickers.deadlineDate.label = this.$t('demand.form.deadlineDate.labelRequired')
                            this.form.datePickers.deadlineDate.clearable = false
                            this.form.datePickers.deadlineDate.rule = [
                                v => !!v || this.$t('demand.form.deadlineDate.required')
                            ]
                        }
                    }
                }
            },
            deep: true
        },
        'form.isExtern': {
            handler: async function (val, oldVal) {
                this.form.isEmployee = !val
                if (val && !this.form.isEdit) {
                    this.getAllManagerCompany()
                }
            },
            deep: true
        }
    },
    mounted () {
        document.getElementById('formdemand').addEventListener('click', this.handleFocus)
        let root = document.documentElement
        root.classList.add('overflow-y-hidden')
        this.configForm()
        if (window.getApp.isEmployee) {
            this.logPreview()
        }
        if (!this.$route.params.id || this.$route.params.id === null) {
            this.form.datePickers.startDate.dateFormatted = this.$options.filters.momentFormatDate(new Date().toISOString().split('T')[0], 'L')
        }
    },
    computed: {
        formMethod () {
            return (this.$route.params.id != null)
        },
        controlColVisibility () {
            if (this.form.isExtern) {
                if (!this.form.isEmployee) {
                    return this.form.isEdit
                }
                return false
            } else {
                return true
            }
            // return (!this.form.isExtern && (this.form.isEmployee || (!this.form.isEmployee && this.form.isEdit)))
        },
        controlColVisibilityTwo () {
            return (!this.form.isEdit)
        },
        showIsExtern () {
            return this.form.selects.manager.items.length > 0
        }
    },
    methods: {
        configPermissions () {
            const currentUserCompany = JSON.parse(localStorage.getItem('currentUserCompany'))
            this.form.isAdmin = this.$helpers.isAdmin()
            if (currentUserCompany.client.objectId !== this.demand.owner.objectId) {
                this.form.isEmployee = false
                this.form.isExtern = true
            } else {
                this.form.isExtern = false
            }

            if (!this.form.isEmployee) {
                if (this.form.isExtern) {
                    this.form.selects.client.readonly = true
                    this.form.datePickers.startDate.readonly = true
                    this.form.datePickers.deadlineDate.readonly = true
                    this.form.selects.responsible.readonly = true
                    this.form.selects.status.readonly = true
                }
            }
        },
        async configForm () {
            const currentUserCompany = JSON.parse(localStorage.getItem('currentUserCompany'))
            this.form.companyType = currentUserCompany.client.type
            this.form.isExtern = (this.form.companyType !== 'manager' && this.form.companyType === 'managed_company')
            this.form.isEmployee = this.$helpers.checkIsEmployee()

            if (this.$route.params.id != null) {
                this.form.title = this.$t('demand.form.titleDetail')
                if (!this.form.isEmployee) {
                    this.form.labels.demand = this.$t('demand.form.demand.label')
                    this.form.datePickers.startDate.label = this.$t('demand.form.startDate.label')
                    this.form.datePickers.startDate.rule = []
                    this.form.datePickers.deadlineDate.rule = []
                    this.form.selects.responsible.label = this.$t('demand.form.responsible.label')
                    this.form.selects.status.label = this.$t('demand.form.status.label')
                }
                this.loadData()
            } else {
                await Promise.all([
                    this.getAllClient(),
                    this.getAllDepartment(),
                    this.getAllEmployee(),
                    this.getAllManagerCompany()
                ])
                this.demand.requester = currentUserCompany.user.name
                if (this.$route.params.requesterType !== undefined) {
                    if (this.$route.params.requesterType === 'juridico') {
                        this.form.selects.responsible.selectedItems = this.$route.params.requesterId
                    } else {
                        this.form.selects.client.selectedItems = this.$route.params.requesterId
                    }
                }

                if (this.form.companyType === 'company' || this.form.companyType === 'managed_company') {
                    this.form.selects.client.selectedItems = currentUserCompany.client.objectId
                }

                if (this.form.companyType === 'department') {
                    this.form.isEmployee = false
                    this.form.isExtern = true
                }

                this.form.loading = false
            }
        },
        close () {
            this.dialog = false
            this.$router.go(-1)
        },
        async getAllManagerCompany () {
            this.form.selects.manager.items = await this.$helpers.getAllManagerCompany()
        },
        async getAllClient () {
            this.form.selects.client.items = await this.$helpers.getAllClient(null, false, true)
        },
        async getAllDepartment () {
            this.form.selects.department.items = await this.$helpers.getAllClientTwo(true, 0, 'department')
        },
        async getAllEmployee () {
            this.form.selects.responsible.items = await this.$helpers.getAllEmployee(this.demand?.owner?.objectId)
        },
        async loadData () {
            let endpoint = API.EndPoints.getDemand
            let params = {
                companyType: this.form.companyType,
                objectId: this.$route.params.id
            }

            if (!this.form.isEmployee) {
                const profile = JSON.parse(localStorage.getItem('profile'))
                params.clientIds = await this.$helpers.getAllUserCompanies(profile, 0).then((results) => {
                    return results.map((company) => {
                        return company.client.objectId
                    })
                })
            } else {
                params.isEmployee = true
            }

            new API(endpoint, params).request().then(async response => {
                if (response.data.result.startDate) {
                    this.form.datePickers.startDate.dateFormatted = this.$options.filters.momentFormatDate(response.data.result.startDate, 'L')
                }
                if (response.data.result.deadlineDate) {
                    if (response.data.result.deadlineDate.iso !== '9000-01-01T00:00:00.000Z') {
                        this.form.datePickers.deadlineDate.dateFormatted = this.$options.filters.momentFormatDate(response.data.result.deadlineDate, 'L')
                    }
                }
                this.demand = Object.assign({}, response.data.result)

                await Promise.all([
                    this.getAllDepartment(),
                    this.getAllEmployee(),
                    this.getAllManagerCompany()
                ])

                this.form.selects.client.items = [this.demand.client]
                this.form.selects.client.selectedItems = this.demand.client.objectId
                this.form.selects.client.readonly = true

                if (this.demand.department !== undefined) {
                    this.form.selects.department.items = [this.demand.department]
                    this.form.selects.department.selectedItems = this.demand.department.objectId
                }

                if (this.demand.owner !== undefined) {
                    const owner = {
                        objectId: this.demand.owner.objectId,
                        manager: this.demand.owner
                    }
                    this.form.selects.manager.items = [owner]
                    this.form.selects.manager.selectedItems = owner.manager.objectId
                    this.form.selects.manager.readonly = true
                }

                if (this.demand.responsibles !== undefined) {
                    this.demand.responsibles.splice(0, 1)
                }
                this.form.selects.responsible.selectedItems = [this.demand.responsible.objectId, ...(this.demand.responsibles || [])]
                this.form.dirtyBkp.responsibles = this.form.selects.responsible.selectedItems
                this.form.selects.status.selectedItems = this.demand.status
                this.form.isEdit = true
                this.form.showUpdate = true
                await this.configPermissions()
                this.form.loading = false
            }, (e) => {
                this.$helpers.requestError(e)
            })
        },
        formatObject (action) {
            const currentUserCompany = JSON.parse(localStorage.getItem('currentUserCompany'))
            this.demand.action = action
            this.demand.companyType = this.form.companyType
            this.demand.isExtern = this.form.isExtern
            this.demand.createdBy = currentUserCompany.objectId

            // this.demand.isEmployee = this.form.isEmployee

            if (this.form.isExtern) {
                this.demand.status = 1
                this.demand.mainAccount = this.$helpers.checkIsMainAccount()
                this.demand.isEmployee = false

                const managerCompany = this.form.selects.manager.items.filter((manager) => {
                    return manager.objectId === this.form.selects.manager.selectedItems
                })[0]
                if (managerCompany !== undefined) this.demand.owner = managerCompany.manager.objectId
                this.demand.client = currentUserCompany.client.objectId

                if (this.form.companyType === 'department') {
                    this.demand.owner = currentUserCompany.parent.objectId
                    this.demand.client = currentUserCompany.parent.objectId
                    this.demand.department = currentUserCompany.client.objectId
                }
            } else {
                if (this.form.selects.client.selectedItems) {
                    const clientSelected = this.form.selects.client.items.filter((client) => {
                        return client.objectId === this.form.selects.client.selectedItems
                    })[0]
                    if (clientSelected !== undefined) this.demand.client = clientSelected.objectId
                }
                if (this.form.selects.department.selectedItems) {
                    const departmentSelected = this.form.selects.department.items.filter((department) => {
                        return department.objectId === this.form.selects.department.selectedItems
                    })[0]
                    if (departmentSelected !== undefined) this.demand.department = departmentSelected.objectId
                }
                if (this.form.datePickers.startDate.dateFormatted) {
                    this.demand.startDate = this.form.datePickers.startDate.dateFormatted
                } else {
                    this.demand.startDate = undefined
                }
                if (this.form.datePickers.deadlineDate.dateFormatted) {
                    this.demand.deadlineDate = this.form.datePickers.deadlineDate.dateFormatted
                } else {
                    this.demand.deadlineDate = new Date('9000-01-01T00:00:00.000Z')
                }
                this.demand.responsible = this.form.selects.responsible.selectedItems[0]
                this.demand.responsibles = this.form.selects.responsible.selectedItems
                this.demand.status = this.form.selects.status.selectedItems
                this.demand.owner = currentUserCompany.client.objectId
            }

            this.demand.responsibleName = this.form.selects.responsible.items
                .filter(item => item.objectId === this.demand.responsible)
                .map(item => item.user.name)[0]

            let params = JSON.stringify(this.demand)
            return params
        },
        async save (boolean) {
            this.btnDisable = true
            if (this.$refs.form.validate()) {
                this.form.valid = false
                if (boolean) {
                    await this.update()
                } else {
                    await this.create()
                }
            } else {
                this.btnDisable = false
                this.form.valid = true
            }
        },
        saveSuccess () {
            if (this.$route.params.requesterType) {
                if (this.$route.params.requesterType === 'juridico') {
                    this.$router.push(`/juridico/detalhe/${this.$route.params.requesterId}/demandas/ativas`).catch(e => {})
                } else if (this.$route.params.requesterType === 'departamentos') {
                    const routers = {
                        CadastrarDemandaDepartamentoAA: this.$route.params.activeTab,
                        DetalheDemandaDepartamentoAtivas: { name: 'DepartamentoAbaInternaDemandasAtivas', params: { id: this.$route.params.requesterId } },
                        DetalheDemandaDepartamentoFinalizadas: { name: 'DepartamentoAbaInternaDemandasFinalizadas', params: { id: this.$route.params.requesterId } },
                        DetalheDemandaDepartamentoStandyBy: { name: 'DepartamentoAbaInternaDemandasStandby', params: { id: this.$route.params.requesterId } }
                    }
                    window.getApp.refreshDemands = true
                    this.$router.push(routers[this.$route.name]).catch(e => {})
                } else if (this.$route.params.requesterType === 'portfolios') {
                    const routers = {
                        CadastrarDemandaPortfolioAA: this.$route.params.activeTab,
                        DetalheDemandaPortfolioAtivas: { name: 'PortfolioAbaInternaDemandasAtivas', params: { id: this.$route.params.requesterId } },
                        DetalheDemandaPortfolioFinalizadas: { name: 'PortfolioAbaInternaDemandasFinalizadas', params: { id: this.$route.params.requesterId } },
                        DetalheDemandaPortfolioStandyBy: { name: 'PortfolioAbaInternaDemandasStandby', params: { id: this.$route.params.requesterId } }
                    }
                    window.getApp.refreshDemands = true
                    this.$router.push(routers[this.$route.name]).catch(e => {})
                } else {
                    this.$router.push(`/notificacoes`).catch(e => {})
                }
            } else {
                window.getApp.refreshDemands = true
                this.$router.push('/demandas').catch(e => {})
            }
        },
        saveError (message) {
            this.btnDisable = false
            alert((message) || this.$t('common.errorSave'))
        },
        async create () {
            this.form.loading = true
            if (this.demand.files.length > 0) {
                await this.uploadDemandFile(this.demand.files)
            }

            this.demand.files = this.tempFiles

            for (let index = 0; index < this.demand.interaction.results.length; index++) {
                const interaction = this.demand.interaction.results[index]
                if (interaction.files.length > 0) {
                    await this.uploadDemandFile(interaction.files)
                }
                this.demand.interaction.results[index].files = this.tempFiles
            }

            let endpoint = API.EndPoints.createDemand
            new API(endpoint, this.formatObject('create')).request()
                .then((response) => {
                    this.saveSuccess()
                }, (error) => {
                    if (error.status !== 0) this.saveError(error.body.error)
                })
        },
        async uploadDemandFile (files) {
            this.tempFiles = []
            for (let index = 0; index < files.length; index++) {
                let endpoint = API.EndPoints.upload
                endpoint.filename = this.$helpers.normalizeFilename(files[index].file.name)
                await new API(endpoint, files[index].file).request()
                    .then(response => {
                        const file = response.data
                        file.__type = 'File'
                        this.tempFiles.push({ file: file })
                    })
            }
        },
        update () {
            let endpoint = API.EndPoints.updateDemand
            new API(endpoint, this.formatObject('update')).request()
                .then((response) => {
                    this.saveSuccess()
                }, (error) => {
                    if (error.status !== 0) this.saveError(error.body.error)
                })
        },
        loadInteractions () {
            this.form.timeline.loadData = true
        },
        handleFocus () {
            this.$eventHub.$emit('handleFocus')
        },
        logPreview () {
            if (this.$route.params.id) {
                let endpoint = API.EndPoints.logPreview

                const params = {
                    demand: this.$route.params.id
                }

                new API(endpoint, params).request().then(
                    response => {},
                    e => {
                        this.$helpers.requestError(e)
                    }
                )
            }
        }
    }
}
</script>

<style lang="sass">
    .v-dialog__content.v-dialog__content--active,
    .v-dialog__content.v-dialog__content--active .full-screen
        min-height: 100vh
</style>
